export interface QuizQuestion {
  id: string;
  question: string;
  options: QuizOption[];
}

export interface QuizOption {
  id: string;
  label: string;
  icon: string;
  styleWeight: {
    minimalist: number;
    rustic: number;
    tech: number;
    maximalist: number;
  };
}

export interface QuizProfile {
  id: string;
  name: string;
  description: string;
  colors: string;
  materials: string;
  icon: string;
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: 'style',
    question: 'quiz.questions.style.question',
    options: [
      {
        id: 'minimalist',
        label: 'quiz.questions.style.options.minimalist',
        icon: '🌿',
        styleWeight: { minimalist: 3, rustic: 0, tech: 1, maximalist: 0 }
      },
      {
        id: 'rustic',
        label: 'quiz.questions.style.options.rustic',
        icon: '🪵',
        styleWeight: { minimalist: 0, rustic: 3, tech: 0, maximalist: 0 }
      },
      {
        id: 'tech',
        label: 'quiz.questions.style.options.tech',
        icon: '📱',
        styleWeight: { minimalist: 1, rustic: 0, tech: 3, maximalist: 0 }
      },
      {
        id: 'maximalist',
        label: 'quiz.questions.style.options.maximalist',
        icon: '✨',
        styleWeight: { minimalist: 0, rustic: 0, tech: 0, maximalist: 3 }
      }
    ]
  },
  {
    id: 'colors',
    question: 'quiz.questions.colors.question',
    options: [
      {
        id: 'neutral',
        label: 'quiz.questions.colors.options.neutral',
        icon: '🤍',
        styleWeight: { minimalist: 3, rustic: 1, tech: 2, maximalist: 0 }
      },
      {
        id: 'traditional',
        label: 'quiz.questions.colors.options.traditional',
        icon: '❤️💚',
        styleWeight: { minimalist: 0, rustic: 2, tech: 0, maximalist: 2 }
      },
      {
        id: 'earthy',
        label: 'quiz.questions.colors.options.earthy',
        icon: '🧡',
        styleWeight: { minimalist: 1, rustic: 3, tech: 0, maximalist: 0 }
      },
      {
        id: 'colorful',
        label: 'quiz.questions.colors.options.colorful',
        icon: '🌈',
        styleWeight: { minimalist: 0, rustic: 0, tech: 1, maximalist: 3 }
      }
    ]
  },
  {
    id: 'materials',
    question: 'quiz.questions.materials.question',
    options: [
      {
        id: 'natural',
        label: 'quiz.questions.materials.options.natural',
        icon: '🌾',
        styleWeight: { minimalist: 2, rustic: 3, tech: 0, maximalist: 0 }
      },
      {
        id: 'shiny',
        label: 'quiz.questions.materials.options.shiny',
        icon: '✨',
        styleWeight: { minimalist: 0, rustic: 0, tech: 2, maximalist: 3 }
      },
      {
        id: 'sustainable',
        label: 'quiz.questions.materials.options.sustainable',
        icon: '♻️',
        styleWeight: { minimalist: 3, rustic: 2, tech: 1, maximalist: 0 }
      },
      {
        id: 'mixed',
        label: 'quiz.questions.materials.options.mixed',
        icon: '🎨',
        styleWeight: { minimalist: 1, rustic: 1, tech: 1, maximalist: 2 }
      }
    ]
  },
  {
    id: 'lighting',
    question: 'quiz.questions.lighting.question',
    options: [
      {
        id: 'warm',
        label: 'quiz.questions.lighting.options.warm',
        icon: '💡',
        styleWeight: { minimalist: 2, rustic: 3, tech: 0, maximalist: 1 }
      },
      {
        id: 'smart',
        label: 'quiz.questions.lighting.options.smart',
        icon: '📲',
        styleWeight: { minimalist: 1, rustic: 0, tech: 3, maximalist: 1 }
      },
      {
        id: 'colorful',
        label: 'quiz.questions.lighting.options.colorful',
        icon: '🌈',
        styleWeight: { minimalist: 0, rustic: 0, tech: 1, maximalist: 3 }
      },
      {
        id: 'minimal',
        label: 'quiz.questions.lighting.options.minimal',
        icon: '🕯️',
        styleWeight: { minimalist: 3, rustic: 1, tech: 0, maximalist: 0 }
      }
    ]
  },
  {
    id: 'tree',
    question: 'quiz.questions.tree.question',
    options: [
      {
        id: 'natural',
        label: 'quiz.questions.tree.options.natural',
        icon: '🌲',
        styleWeight: { minimalist: 1, rustic: 3, tech: 0, maximalist: 1 }
      },
      {
        id: 'artificial-premium',
        label: 'quiz.questions.tree.options.artificialPremium',
        icon: '🎄',
        styleWeight: { minimalist: 2, rustic: 0, tech: 2, maximalist: 2 }
      },
      {
        id: 'artificial-sustainable',
        label: 'quiz.questions.tree.options.artificialSustainable',
        icon: '♻️',
        styleWeight: { minimalist: 3, rustic: 1, tech: 2, maximalist: 0 }
      },
      {
        id: 'alternative',
        label: 'quiz.questions.tree.options.alternative',
        icon: '🎨',
        styleWeight: { minimalist: 2, rustic: 1, tech: 3, maximalist: 1 }
      }
    ]
  }
];

export const quizProfiles: Record<string, QuizProfile> = {
  minimalist: {
    id: 'minimalist',
    name: 'quiz.profiles.minimalist.name',
    description: 'quiz.profiles.minimalist.description',
    colors: 'quiz.profiles.minimalist.colors',
    materials: 'quiz.profiles.minimalist.materials',
    icon: '🌿'
  },
  rustic: {
    id: 'rustic',
    name: 'quiz.profiles.rustic.name',
    description: 'quiz.profiles.rustic.description',
    colors: 'quiz.profiles.rustic.colors',
    materials: 'quiz.profiles.rustic.materials',
    icon: '🪵'
  },
  tech: {
    id: 'tech',
    name: 'quiz.profiles.tech.name',
    description: 'quiz.profiles.tech.description',
    colors: 'quiz.profiles.tech.colors',
    materials: 'quiz.profiles.tech.materials',
    icon: '📱'
  },
  maximalist: {
    id: 'maximalist',
    name: 'quiz.profiles.maximalist.name',
    description: 'quiz.profiles.maximalist.description',
    colors: 'quiz.profiles.maximalist.colors',
    materials: 'quiz.profiles.maximalist.materials',
    icon: '✨'
  }
};

export const calculateProfile = (answers: Record<string, string>): string => {
  const scores = {
    minimalist: 0,
    rustic: 0,
    tech: 0,
    maximalist: 0
  };

  Object.entries(answers).forEach(([questionId, optionId]) => {
    const question = quizQuestions.find(q => q.id === questionId);
    const option = question?.options.find(o => o.id === optionId);
    
    if (option) {
      scores.minimalist += option.styleWeight.minimalist;
      scores.rustic += option.styleWeight.rustic;
      scores.tech += option.styleWeight.tech;
      scores.maximalist += option.styleWeight.maximalist;
    }
  });

  const maxScore = Math.max(...Object.values(scores));
  const winner = Object.entries(scores).find(([_, score]) => score === maxScore)?.[0];
  
  return winner || 'minimalist';
};
